import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { MapPin, Eye, CircleDot, CheckCircle2, Wrench } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEP_LABELS = {
  on_duty: 'On Duty',
  attendance: 'Attendance',
  depot_arrival: 'Depot Arrival',
  depot_departure: 'Depot Departure',
  vehicle_inspection: 'Vehicle Inspection',
  fault_reporting: 'Fault Reporting',
  workshop_job_cards: 'Workshop Job Cards',
  return_to_depot: 'Return to Depot',
  off_duty: 'Off Duty',
};
const STEP_KEYS = Object.keys(STEP_LABELS);

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—');
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

export default function DriverShiftsAdmin() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().slice(0, 10));
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailShift, setDetailShift] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterDate) params.shift_date = filterDate;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/driver-shifts`, { headers, params });
      setShifts(res.data || []);
    } catch (err) {
      toast.error('Failed to load shifts');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDate, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="driver-shifts-admin">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">Driver Shifts</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Live view of every driver's day — On Duty → Off Duty. GPS + timestamps for each step.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="h-9 w-40" data-testid="date-filter" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded border px-2 text-sm" data-testid="status-filter">
              <option value="all">All statuses</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Shifts on {filterDate}</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="p-6 text-center text-muted-foreground">Loading…</div>
            ) : shifts.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">No shifts recorded for this date.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Driver</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Current Step</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Start KM</TableHead>
                    <TableHead className="text-right">End KM</TableHead>
                    <TableHead className="text-right">Distance</TableHead>
                    <TableHead className="text-right">Ended</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shifts.map((s) => {
                    const doneCount = STEP_KEYS.filter(k => s.steps?.[k]).length;
                    return (
                      <TableRow key={s.id} data-testid={`shift-row-${s.id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{s.driver_name}</span>
                            {s.vehicle_registration && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200"
                                data-testid={`shift-vehicle-${s.id}`}
                                title="Vehicle on shift"
                              >
                                🚚 {s.vehicle_registration}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-amber-700">{s.vehicle_registration || '—'}</TableCell>
                        <TableCell className="text-sm">{fmtTime(s.started_at)}</TableCell>
                        <TableCell className="text-sm">{STEP_LABELS[s.current_step] || (s.status === 'completed' ? '—' : 'On Duty')}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500" style={{ width: `${(doneCount / STEP_KEYS.length) * 100}%` }} />
                            </div>
                            <span className="text-[10px] text-muted-foreground">{doneCount}/{STEP_KEYS.length}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {s.status === 'in_progress' ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 gap-1"><CircleDot className="h-3 w-3 animate-pulse" /> In Progress</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-700 border-slate-300 gap-1"><CheckCircle2 className="h-3 w-3" /> Completed</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">{s.start_km ? Number(s.start_km).toLocaleString('en-ZA') : '—'}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{s.end_km ? Number(s.end_km).toLocaleString('en-ZA') : '—'}</TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold text-emerald-700">{s.distance_km ? Number(s.distance_km).toLocaleString('en-ZA') + ' km' : '—'}</TableCell>
                        <TableCell className="text-right text-sm">{fmtTime(s.ended_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => setDetailShift(s)} data-testid={`view-shift-${s.id}`}>
                            <Eye className="h-3.5 w-3.5 mr-1" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Shift detail */}
      <Dialog open={!!detailShift} onOpenChange={(open) => { if (!open) setDetailShift(null); }}>
        <DialogContent className="max-w-2xl" data-testid="shift-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detailShift?.driver_name} · {detailShift?.vehicle_registration || 'No vehicle'}</DialogTitle>
            <DialogDescription>
              {detailShift?.shift_date} · started {fmtDT(detailShift?.started_at)} {detailShift?.ended_at && `· ended ${fmtDT(detailShift?.ended_at)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {STEP_KEYS.map((key) => {
              const s = detailShift?.steps?.[key];
              if (!s) return null;
              return (
                <div key={key} className="border rounded p-2 bg-slate-50">
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="font-semibold">{STEP_LABELS[key]}</span>
                    <span className="text-xs text-muted-foreground">{fmtDT(s.ts)}</span>
                  </div>
                  {s.lat != null && (
                    <a
                      href={`https://www.google.com/maps?q=${s.lat},${s.lng}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline mt-1"
                    >
                      <MapPin className="h-3 w-3" /> {s.lat.toFixed(5)}, {s.lng.toFixed(5)}
                      {s.accuracy != null && <span className="text-muted-foreground">(±{Math.round(s.accuracy)}m)</span>}
                    </a>
                  )}
                  {s.notes && <div className="text-xs mt-1"><b>Notes:</b> {s.notes}</div>}
                  {s.odometer_km != null && <div className="text-xs mt-1"><b>Odometer:</b> {s.odometer_km} km</div>}
                  {s.checks && (
                    <div className="text-xs mt-1">
                      <b>Checks:</b>{' '}
                      {Object.entries(s.checks).filter(([, v]) => v).map(([k]) => k).join(', ') || '—'}
                      {s.overall_ok != null && (
                        <span className={`ml-2 ${s.overall_ok ? 'text-emerald-700' : 'text-rose-700'}`}>
                          [{s.overall_ok ? 'Roadworthy' : 'NOT roadworthy'}]
                        </span>
                      )}
                    </div>
                  )}
                  {s.faults && s.faults.length > 0 && (
                    <div className="text-xs mt-1 space-y-1">
                      <b className="flex items-center gap-1"><Wrench className="h-3 w-3" /> Faults reported ({s.faults.length}):</b>
                      {s.faults.map((f, i) => (
                        <div key={i} className="ml-3">• {f.description} <Badge variant="outline" className="ml-1 text-[9px]">{f.severity}</Badge></div>
                      ))}
                    </div>
                  )}
                  {s.job_card_refs && s.job_card_refs.length > 0 && (
                    <div className="text-xs mt-1"><b>Job Cards:</b> {s.job_card_refs.join(', ')}</div>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
